import Component from '@glimmer/component'
import { service } from '@ember/service'
import { isEmpty, isPresent } from '@ember/utils'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { AllGradesOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import { AllClassesOption } from 'teacher-ui-client/components/base/select/school-class/options'
import { uniqBy } from 'lodash'
import { type IntlService, t } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { BaseTableActions } from 'teacher-ui-client/components/base/table-actions'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import SelectableCell from 'teacher-ui-client/components/stable-table/basic-cells/selectable'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { GradeSelect } from 'teacher-ui-client/components/base/select/grade-position'
import { SchoolClassSelect } from 'teacher-ui-client/components/base/select/school-class'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import { array } from '@ember/helper'
import { on } from '@ember/modifier'
import { pick } from 'teacher-ui-client/helpers/pick'
import { optional } from 'teacher-ui-client/helpers/optional'
import { fn } from '@ember/helper'
import type { FilterDataItem } from '@blakeelearning/data-tables/utils/filtering'
import type Student from 'teacher-ui-client/models/student'
import type { RowId } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Args {
  students?: Student[]
  subscriptionType: SubscriptionType
  gradePosition?: number
  schoolClassId?: string
  themeKey?: ThemeKeys
  searchString?: string
  selectedStudentIds?: string[]
  sortingConfig?: SortingConfig
  paginationConfig?: PaginationConfig
  onSortClick?: (sortingConfig: SortingConfig) => void
  onSelectClick?: (studentIds: RowId[]) => void
  onSearchInput?: (value: string) => void
  onGradePositionSelect?: (gradePosition: number) => void
  onSchoolClassIdSelect?: (schoolClassId: string) => void
}

interface Signature {
  Args: Args
}

interface StudentItem extends FilterDataItem {
  id: string
  firstName: string
  lastName: string
  gradePosition: number
  gradeName?: string
  schoolClassIds: string[]
  schoolClassesJoined: string
  searchText: string
}

interface Theme {
  controlsBgColor: string
  tableHeaderBgColor: string
  rowHoverColor: string
}

export class ShareCollectionTable extends Component<Signature> {
  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  get students() {
    return this.args.students ?? []
  }

  get subscriptionType() {
    return this.args.subscriptionType
  }

  get gradePosition() {
    return this.args.gradePosition ?? AllGradesOption.id
  }

  get schoolClassId() {
    return this.args.schoolClassId ?? AllClassesOption.id
  }

  get selectedTheme() {
    const themes: Partial<Record<ThemeKeys, Theme>> = {
      [ThemeKeys.blue]: {
        controlsBgColor: 'bg-oceany-blue-150',
        tableHeaderBgColor: 'bg-oceany-blue-100',
        rowHoverColor: 'group-hover:bg-oceany-blue-100',
      },
      [ThemeKeys.green]: {
        controlsBgColor: 'bg-ms-green-150',
        tableHeaderBgColor: 'bg-ms-green-100',
        rowHoverColor: 'group-hover:bg-ms-green-100',
      },
      [ThemeKeys.default]: {
        controlsBgColor: 'bg-dusty-black-100',
        tableHeaderBgColor: 'bg-dusty-black-50',
        rowHoverColor: 'group-hover:bg-dusty-black-50',
      },
    }
    return themes[this.args.themeKey ?? ThemeKeys.default]
  }

  // massage data / map students into items, before handing over to the table data transformer
  get studentItems(): StudentItem[] {
    const { subscriptionType } = this
    return this.students
      .filter(hasId)
      .map((student) => {
        const schoolClasses = this.studentSchoolClasses(student, subscriptionType)
        const schoolClassIds = schoolClasses.filter(hasId).map((schoolClass) => schoolClass.id)
        const schoolClassesJoined = schoolClasses.map((schoolClass) => schoolClass.name).join(', ')
        return {
          id: student.id,
          firstName: student.firstName,
          lastName: student.lastName,
          gradePosition: student.gradePosition,
          gradeName: this.gradeSets.nameFromGradeSetByPosition(student.gradePosition),
          schoolClassIds,
          schoolClassesJoined,
          searchText: `${student.firstName} ${student.lastName} ${schoolClassesJoined}`,
        }
      })
      .filter((studentItem) => {
        // remove any unallocated students
        return !isEmpty(studentItem.schoolClassIds)
      })
  }

  AllGradesOption = AllGradesOption
  AllClassesOption = AllClassesOption

  get gradePositions() {
    return this.gradeSets.grades.map((grade) => grade.position)
  }

  get schoolClasses() {
    const schoolClasses = this.students.flatMap((student) => this.studentSchoolClasses(student, this.subscriptionType))
    return uniqBy(schoolClasses, (schoolClass) => schoolClass.id)
  }

  studentSchoolClasses(student: Student, subscriptionType: SubscriptionType) {
    let schoolClasses: SchoolClass[]
    switch (subscriptionType) {
      case SubscriptionType.Reading:
        schoolClasses = student.readingSchoolClasses
        break
      case SubscriptionType.Maths:
        schoolClasses = student.mathsSchoolClasses
        break
      default:
        schoolClasses = []
    }
    return schoolClasses
  }

  get groupFilterConfig() {
    const { gradePosition, schoolClassId } = this
    // Only need to apply a group filter if there is grade position or school class
    if (gradePosition === AllGradesOption.id && schoolClassId === AllClassesOption.id) return null

    const itemFilter = (item: StudentItem) => {
      // return true if student is in class and has grade position
      const matchesGradePosition = gradePosition === AllGradesOption.id ? true : item.gradePosition === gradePosition
      const matchesSchoolClass =
        schoolClassId === AllClassesOption.id ? true : item.schoolClassIds?.includes(schoolClassId) ? true : false

      return matchesGradePosition && matchesSchoolClass
    }

    return { itemFilter }
  }

  get textFilterConfig() {
    const { searchString } = this.args
    // Only need to apply a text filter if there is text
    if (isEmpty(searchString)) return null
    if (typeof searchString !== 'string') return null

    const lowercasedSearchString = searchString.toLowerCase()
    const itemFilter = (item: StudentItem) => item.searchText.toLowerCase().includes(lowercasedSearchString)

    return { itemFilter }
  }

  get filterConfigs() {
    return [this.groupFilterConfig, this.textFilterConfig].filter(isPresent)
  }

  get columns() {
    const { selectedTheme } = this

    const cellBorders = 'border-b border-t border-l-0 border-r-0 border-dusty-black-100'

    const sortColumn = {
      component: SortableCell,
      classNames: `table-box table-box-header ${selectedTheme?.tableHeaderBgColor}`,
      cellClassNames: `table-box table-box-cell ${cellBorders} ${selectedTheme?.rowHoverColor}`,
    }

    return [
      {
        value: this.intl.t(`bookLibrary.components.shareCollectionTable.headers.selectAll`),
        component: SelectableCell,
        classNames: `table-box p-0 w-px ${selectedTheme?.tableHeaderBgColor}`,
        componentClassNames: 'table-box-header',
        cellComponent: SelectableCell,
        cellClassNames: `table-box ${cellBorders} ${selectedTheme?.rowHoverColor}`,
        cellComponentClassNames: 'table-box-cell',
        valuePath: '',
      },
      {
        ...sortColumn,
        value: this.intl.t('bookLibrary.components.shareCollectionTable.headers.firstName'),
        valuePath: 'firstName',
        comparerKey: 'sortByValue',
        classNames: `${sortColumn.classNames} text-left w-1/6`,
        cellClassNames: `${sortColumn.cellClassNames} text-left`,
      },
      {
        ...sortColumn,
        value: this.intl.t('bookLibrary.components.shareCollectionTable.headers.lastName'),
        valuePath: 'lastName',
        comparerKey: 'sortByValue',
        classNames: `${sortColumn.classNames} text-left w-1/6`,
        cellClassNames: `${sortColumn.cellClassNames} text-left`,
      },
      {
        ...sortColumn,
        value: this.intl.t('bookLibrary.components.shareCollectionTable.headers.grade'),
        valuePath: 'gradeName',
        sortKey: 'gradePosition',
        comparerKey: 'sortByGrade',
        classNames: `${sortColumn.classNames} text-center w-px`,
        cellClassNames: `${sortColumn.cellClassNames} text-center`,
      },
      {
        ...sortColumn,
        value: this.intl.t('bookLibrary.components.shareCollectionTable.headers.schoolClasses'),
        valuePath: 'schoolClassesJoined',
        comparerKey: 'sortByValue',
        classNames: `${sortColumn.classNames} text-left w-4/6`,
        cellClassNames: `${sortColumn.cellClassNames} text-left`,
      },
    ]
  }

  <template>
    <BaseTableActions
      @items={{this.studentItems}}
      @columns={{this.columns}}
      @selectedItemIds={{@selectedStudentIds}}
      @sortingConfig={{@sortingConfig}}
      @paginationConfig={{@paginationConfig}}
      @filterConfigs={{this.filterConfigs}}
      @onSortClick={{@onSortClick}}
      @onSelectClick={{@onSelectClick}}
      as |Table state paging|
    >
      <table-controls
        class="grid grid-cols-4 gap-x-2 border-t border-b border-white p-4 {{this.selectedTheme.controlsBgColor}}"
      >
        <div>
          <InputText
            class="w-full"
            type="search"
            role="searchbox"
            value={{@searchString}}
            placeholder={{t "bookLibrary.components.shareCollectionTable.searchInputPlaceholder"}}
            aria-label={{t "bookLibrary.components.shareCollectionTable.searchInputPlaceholder"}}
            @themeKey={{@themeKey}}
            @icon="search"
            @iconEnd={{true}}
            {{on "input" (pick "target.value" (optional @onSearchInput))}}
          />
        </div>

        <GradeSelect
          @additionalOptions={{array this.AllGradesOption}}
          @gradePositions={{this.gradePositions}}
          @gradePosition={{@gradePosition}}
          @updateGradePosition={{optional @onGradePositionSelect}}
          @themeKey={{@themeKey}}
        />

        <SchoolClassSelect
          @additionalOptions={{array this.AllClassesOption}}
          @schoolClasses={{this.schoolClasses}}
          @schoolClassId={{@schoolClassId}}
          @updateSchoolClassId={{optional @onSchoolClassIdSelect}}
          @themeKey={{@themeKey}}
        />

        <div class="text-right">
          <button
            type="reset"
            class="p-2 font-semibold"
            {{on "click" (fn (optional @onSearchInput) "")}}
            {{on "click" (fn (optional @onGradePositionSelect) this.AllGradesOption.id)}}
            {{on "click" (fn (optional @onSchoolClassIdSelect) this.AllClassesOption.id)}}
            {{on "click" (fn (optional @onSelectClick) (array))}}
          >
            {{t "bookLibrary.components.shareCollectionTable.resetButton"}}
          </button>
        </div>
      </table-controls>

      {{! Table }}
      <Table class="w-full" as |tbl|>
        <tbl.head />
        {{#if state.isEmptyAndIdle}}
          <tbl.spanner class="p-3 text-center italic">
            {{t "bookLibrary.components.shareCollectionTable.emptyMessage"}}
          </tbl.spanner>
        {{else}}
          {{#if state.isTransforming}}
            <tbl.spanner
              class="table-box table-box-cell border-transparent
                {{unless state.isEmpty 'absolute left-1/2 -translate-x-1/2 transform'}}"
            >
              <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
            </tbl.spanner>
          {{/if}}
          <tbl.body class={{if state.isTransforming "opacity-50"}} as |b|>
            <b.row class="group" />
          </tbl.body>
        {{/if}}
      </Table>

      {{! Paging }}
      {{#if paging.pageCount}}
        <BasePageSelector class="mx-2 my-4" @pageCount={{paging.pageCount}} @itemCount={{this.studentItems.length}} />
      {{/if}}
    </BaseTableActions>
  </template>
}

export default ShareCollectionTable
